import React, { useState, useRef, useEffect, useCallback } from 'react';
import { UploadIcon, ArrowLeftIcon, BackpackIcon, CheckIcon, GridIcon } from '@radix-ui/react-icons';
import axios from 'axios';
import GenerationProgress from './GenerationProgress';
import useScrollIntoView from './useScrollIntoView';
import { useNotifications } from './Notifications';
import { useI18n } from '../i18n';

const ClothingTryOn = ({
  user,
  credits,
  setCredits,
  getAuthHeaders,
  backendUrl,
  onBack,
  setImageListRefresh,
  setShowPayment
}) => {
  const { t } = useI18n();
  const [personImage, setPersonImage] = useState(null);
  const [personPreview, setPersonPreview] = useState(null);
  const [clothingImage, setClothingImage] = useState(null);
  const [clothingPreview, setClothingPreview] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);

  // Outfit library state
  const [useLibrary, setUseLibrary] = useState(false);
  const [outfitLibrary, setOutfitLibrary] = useState({});
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedOutfit, setSelectedOutfit] = useState(null);
  const [loadingLibrary, setLoadingLibrary] = useState(false);

  const personInputRef = useRef(null);
  const clothingInputRef = useRef(null);
  const resultRef = useScrollIntoView(result);
  const { notifyWarning, notifyError, notifyApiError } = useNotifications();

  const fetchOutfitLibrary = useCallback(async () => {
    setLoadingLibrary(true);
    try {
      const response = await axios.get(`${backendUrl}/api/outfit-library`);
      if (response.data.success) {
        setOutfitLibrary(response.data.library || {});
        setCategories(response.data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching outfit library:', error);
    } finally {
      setLoadingLibrary(false);
    }
  }, [backendUrl]);

  // Fetch outfit library on mount
  useEffect(() => {
    fetchOutfitLibrary();
  }, [fetchOutfitLibrary]);

  const handleImageSelect = (event, type) => {
    const file = event.target.files[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        notifyWarning('That image is larger than 10MB. Please choose a smaller one.');
        return;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        notifyWarning('That file is not an image. Please choose a JPG or PNG.');
        return;
      }

      if (type === 'person') {
        setPersonImage(file);

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          setPersonPreview(e.target.result);
        };
        reader.readAsDataURL(file);
      } else if (type === 'clothing') {
        setClothingImage(file);
        setSelectedOutfit(null); // Clear library selection

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          setClothingPreview(e.target.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleOutfitSelect = (outfit) => {
    setSelectedOutfit(outfit);
    setClothingImage(null);
    setClothingPreview(null);
    if (clothingInputRef.current) {
      clothingInputRef.current.value = '';
    }
  };

  const handleTryOnClothing = async () => {
    if (isProcessing) return;

    if (!personImage) {
      notifyWarning('Please select a photo of the person first.');
      return;
    }

    if (!useLibrary && !clothingImage) {
      notifyWarning('Please select a clothing image.');
      return;
    }

    if (useLibrary && !selectedOutfit) {
      notifyWarning('Please select an outfit from the library.');
      return;
    }

    if (!user) {
      notifyWarning('Please sign in to use Clothing Try-On.');
      return;
    }

    // credits is null until the balance loads - only a real zero blocks the request.
    if (typeof credits === 'number' && credits <= 0) {
      setShowPayment(true);
      return;
    }

    setIsProcessing(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('personImage', personImage);

      if (useLibrary && selectedOutfit) {
        // Use outfit from library
        formData.append('outfitId', selectedOutfit.id);
      } else {
        // Use custom uploaded clothing
        formData.append('clothingImage', clothingImage);
      }

      // Hardcoded effective prompt for safe virtual try-on
      const prompt = `This is a legitimate virtual clothing try-on application. The first image contains a person who wants to see how different clothing would look on them. The second image contains clothing items (shirts, dresses, jackets, etc.) from a clothing catalog or store. Please create a realistic image showing the person from the first image wearing the clothing from the second image. This is for fashion and shopping purposes only.`;

      formData.append('prompt', prompt);

      const authHeaders = await getAuthHeaders();
      const response = await axios.post(`${backendUrl}/api/clothing-tryon`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...authHeaders
        },
      });

      setResult(response.data.result);
      setCredits(response.data.creditsRemaining);

      // Trigger image list refresh
      setImageListRefresh(prev => prev + 1);
    } catch (error) {
      console.error('Error trying on clothing:', error);
      const described = notifyApiError(error, 'Could not create the try-on image. Please try again.');
      // 400 and 402 are both used for insufficient credits today; the code is the durable signal.
      if (described.code === 'INSUFFICIENT_CREDITS' || described.status === 402 || described.status === 400) {
        setShowPayment(true);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const clearSelection = () => {
    setPersonImage(null);
    setPersonPreview(null);
    setClothingImage(null);
    setClothingPreview(null);
    setSelectedOutfit(null);
    setResult(null);
    if (personInputRef.current) {
      personInputRef.current.value = '';
    }
    if (clothingInputRef.current) {
      clothingInputRef.current.value = '';
    }
  };

  const downloadImage = (imageData, filename = 'clothing-tryon.png') => {
    if (!imageData || !imageData.startsWith('data:image')) {
      notifyError('No image data available to download.');
      return;
    }

    const link = document.createElement('a');
    link.href = imageData;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get filtered outfits based on selected category
  const getFilteredOutfits = () => {
    if (selectedCategory === 'all') {
      const allOutfits = [];
      Object.values(outfitLibrary).forEach(category => {
        if (category.items) {
          allOutfits.push(...category.items);
        }
      });
      return allOutfits;
    }

    return outfitLibrary[selectedCategory]?.items || [];
  };

  const filteredOutfits = getFilteredOutfits();

  return (
    <div className="clothing-tryon">
      <div className="feature-header">
        <button className="btn btn-secondary back-btn" onClick={onBack}>
          <ArrowLeftIcon style={{ marginRight: '8px' }} />
          {t('nav.backToDashboard')}
        </button>
        <div className="header-content">
          <div className="feature-icon">
            <BackpackIcon style={{ width: '32px', height: '32px' }} />
          </div>
          <div>
            <h1>Virtual Clothing Try-On</h1>
            <p>See how clothing looks on you with AI-powered virtual try-on</p>
          </div>
        </div>
      </div>

      <div className="clothing-tryon-content">
        {/* Selection Mode Toggle */}
        <div className="selection-toggle">
          <button
            className={`toggle-btn ${!useLibrary ? 'active' : ''}`}
            onClick={() => setUseLibrary(false)}
            disabled={isProcessing}
          >
            <UploadIcon style={{ marginRight: '8px' }} />
            {t('action.uploadCustom')}
          </button>
          <button
            className={`toggle-btn ${useLibrary ? 'active' : ''}`}
            onClick={() => setUseLibrary(true)}
            disabled={isProcessing}
          >
            <GridIcon style={{ marginRight: '8px' }} />
            {t('action.browseLibrary')}
          </button>
        </div>

        <div className="upload-grid">
          {/* Person Photo Upload - Always shown */}
          <div className="upload-column">
            <h3>1. Upload Person Photo</h3>
            {!personPreview ? (
              <div
                className="upload-area"
                onClick={() => personInputRef.current?.click()}
              >
                <UploadIcon style={{ width: '48px', height: '48px', marginBottom: '16px' }} />
                <h4>Upload Person Photo</h4>
                <p>{t('upload.selectImage')}</p>
                <p className="upload-hint">{t('upload.formatsShort')}</p>
              </div>
            ) : (
              <div className="image-preview-wrapper">
                <img src={personPreview} alt="Person" className="preview-image" />
                <button
                  className="btn btn-secondary change-btn"
                  onClick={() => personInputRef.current?.click()}
                >
                  Change Photo
                </button>
              </div>
            )}
            <input
              ref={personInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleImageSelect(e, 'person')}
              style={{ display: 'none' }}
            />
          </div>

          {/* Clothing Selection - Changes based on mode */}
          <div className="upload-column">
            <h3>2. {useLibrary ? 'Select from Library' : 'Upload Clothing Photo'}</h3>

            {!useLibrary ? (
              // Custom upload mode
              <>
                {!clothingPreview ? (
                  <div
                    className="upload-area"
                    onClick={() => clothingInputRef.current?.click()}
                  >
                    <UploadIcon style={{ width: '48px', height: '48px', marginBottom: '16px' }} />
                    <h4>Upload Clothing Photo</h4>
                    <p>{t('upload.selectImage')}</p>
                    <p className="upload-hint">{t('upload.formatsShort')}</p>
                  </div>
                ) : (
                  <div className="image-preview-wrapper">
                    <img src={clothingPreview} alt="Clothing" className="preview-image" />
                    <button
                      className="btn btn-secondary change-btn"
                      onClick={() => clothingInputRef.current?.click()}
                    >
                      Change Clothing
                    </button>
                  </div>
                )}
                <input
                  ref={clothingInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageSelect(e, 'clothing')}
                  style={{ display: 'none' }}
                />
              </>
            ) : (
              // Library mode
              <div className="outfit-library-section">
                {/* Category Filter */}
                <div className="category-filter">
                  <button
                    className={`category-btn ${selectedCategory === 'all' ? 'active' : ''}`}
                    onClick={() => setSelectedCategory('all')}
                  >
                    All
                  </button>
                  {categories.map(cat => (
                    <button
                      key={cat.id}
                      className={`category-btn ${selectedCategory === cat.id ? 'active' : ''}`}
                      onClick={() => setSelectedCategory(cat.id)}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>

                {/* Outfit Grid */}
                {loadingLibrary ? (
                  <div className="loading-library">Loading outfits...</div>
                ) : filteredOutfits.length > 0 ? (
                  <div className="outfit-grid">
                    {filteredOutfits.map(outfit => (
                      <div
                        key={outfit.id}
                        className={`outfit-card ${selectedOutfit?.id === outfit.id ? 'selected' : ''}`}
                        onClick={() => handleOutfitSelect(outfit)}
                      >
                        <img
                          src={outfit.thumbnailUrl}
                          alt={outfit.name}
                          className="outfit-thumbnail"
                        />
                        <div className="outfit-info">
                          <p className="outfit-name">{outfit.name}</p>
                          {outfit.tags && outfit.tags.length > 0 && (
                            <div className="outfit-tags">
                              {outfit.tags.slice(0, 2).map((tag, idx) => (
                                <span key={idx} className="outfit-tag">{tag}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        {selectedOutfit?.id === outfit.id && (
                          <div className="selected-badge">
                            <CheckIcon />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-outfits">
                    <p>No outfits available in this category yet.</p>
                    <p>Try uploading a custom clothing image instead.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Show selected outfit preview if using library */}
        {useLibrary && selectedOutfit && (
          <div className="selected-outfit-preview">
            <h4>Selected Outfit:</h4>
            <div className="selected-outfit-card">
              <img src={selectedOutfit.thumbnailUrl} alt={selectedOutfit.name} />
              <span>{selectedOutfit.name}</span>
            </div>
          </div>
        )}

        {((personPreview && clothingPreview) || (personPreview && useLibrary && selectedOutfit)) && (
          <div className="action-section">
            <div className="action-buttons">
              <button
                className="btn btn-secondary"
                onClick={clearSelection}
                disabled={isProcessing}
              >
                Clear All
              </button>
              <button
                className="btn btn-primary process-btn"
                onClick={handleTryOnClothing}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <div className="spinner" />
                    Processing...
                  </>
                ) : (
                  <>
                    Try On Clothing (1 Credit)
                  </>
                )}
              </button>
            </div>

            <div className="tips">
              <h4>Tips for best results:</h4>
              <ul>
                <li>Use a clear, front-facing photo of the person</li>
                <li>Ensure the clothing image shows the full garment</li>
                <li>Better lighting in both photos produces better results</li>
                <li>The person should be standing or in a clear pose</li>
                <li>Avoid heavily patterned backgrounds</li>
              </ul>
            </div>
          </div>
        )}

        {isProcessing && (
          <GenerationProgress
            title="Creating your virtual try-on"
            estimateSeconds={30}
            steps={[
              'Uploading your photos...',
              'Sending them to the AI model...',
              'Fitting the garment to the person...',
              'Rendering the final image...'
            ]}
          />
        )}

        {result && (
          <div className="result-container" ref={resultRef}>
            <h3>Virtual Try-On Result:</h3>
            {result.startsWith('data:image') ? (
              <>
                <img
                  src={result}
                  alt="Clothing try-on result"
                  className="result-image"
                />
                <div className="result-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => downloadImage(result, `clothing-tryon-${Date.now()}.png`)}
                  >
                    Download Result
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={clearSelection}
                  >
                    Try Another Outfit
                  </button>
                </div>
              </>
            ) : (
              <p className="error-message">{result}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClothingTryOn;
